/**
 * Pre-scan Quality Checks and Same Conditions Check
 * 
 * This module ensures users get consistent results by:
 * 1. Requiring 3 angles per scan (front, left, right)
 * 2. Checking brightness and face rotation
 * 3. Comparing current scan conditions to previous scan
 * 4. Providing warnings when conditions differ significantly
 */

import type { PhotoMetadata } from './photoProcessing';
import type { ScanRecord } from '../services/scanService';

export interface PreScanQualityResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  normalizedBrightness?: number;
  faceRotation?: number;
}

export interface SameConditionsCheckResult {
  shouldWarn: boolean;
  message?: string;
  differences: {
    lighting?: 'brighter' | 'dimmer' | 'similar';
    distance?: 'closer' | 'farther' | 'similar';
    angle?: 'different' | 'similar';
  };
}

/**
 * Check if all required angles are present
 */
export function checkRequiredAngles(
  frontMeta?: PhotoMetadata,
  leftSideMeta?: PhotoMetadata,
  rightSideMeta?: PhotoMetadata
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!frontMeta) {
    errors.push('Front photo is required for accurate analysis.');
  }

  if (!leftSideMeta) {
    errors.push('Left side photo is required for accurate analysis.');
  }

  if (!rightSideMeta) {
    errors.push('Right side photo is required for accurate analysis.');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Normalize brightness across photos
 * Returns normalized brightness value (0-255 scale)
 */
export function normalizeBrightness(
  photos: (PhotoMetadata | undefined)[]
): number | undefined {
  const brightnessValues = photos
    .map((p) => p?.brightness)
    .filter((v): v is number => typeof v === 'number');

  if (brightnessValues.length === 0) {
    return undefined;
  }

  const avgBrightness =
    brightnessValues.reduce((sum, v) => sum + v, 0) / brightnessValues.length;

  // Normalize to optimal range (70-120) if possible
  // This is a simple normalization - in production, you'd use actual image processing
  if (avgBrightness < 50) {
    // Too dark - suggest user retake
    return avgBrightness;
  } else if (avgBrightness > 150) {
    // Too bright - suggest user retake
    return avgBrightness;
  }

  return avgBrightness;
}

/**
 * Estimate face rotation from photo metadata
 * This is a simplified check - in production, you'd use face detection landmarks
 */
export function estimateFaceRotation(meta?: PhotoMetadata): number | undefined {
  // In a real implementation, this would analyze face landmarks
  // For now, we'll use a placeholder that returns undefined
  // This would be implemented with actual face detection library
  return undefined;
}

/**
 * Perform pre-scan quality checks
 */
export function performPreScanQualityCheck(
  frontMeta?: PhotoMetadata,
  leftSideMeta?: PhotoMetadata,
  rightSideMeta?: PhotoMetadata
): PreScanQualityResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required angles
  const angleCheck = checkRequiredAngles(frontMeta, leftSideMeta, rightSideMeta);
  if (!angleCheck.valid) {
    errors.push(...angleCheck.errors);
  }

  // Check brightness
  const photos = [frontMeta, leftSideMeta, rightSideMeta];
  const normalizedBrightness = normalizeBrightness(photos);

  if (normalizedBrightness !== undefined) {
    if (normalizedBrightness < 50) {
      warnings.push('Lighting looks dim. Try a brighter room for clearer analysis.');
    } else if (normalizedBrightness > 150) {
      warnings.push('Lighting looks too bright. Try softer, natural lighting.');
    }
  }

  // Check face rotation (placeholder - would use actual face detection)
  const faceRotation = estimateFaceRotation(frontMeta);
  if (faceRotation !== undefined && Math.abs(faceRotation) > 15) {
    warnings.push('Face appears rotated. Try to keep your face straight and centered.');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    normalizedBrightness,
    faceRotation,
  };
}

/**
 * Compare current scan conditions to previous scan
 */
export function checkSameConditions(
  currentPhotos: {
    front?: PhotoMetadata;
    leftSide?: PhotoMetadata;
    rightSide?: PhotoMetadata;
  },
  previousScan?: ScanRecord | null
): SameConditionsCheckResult {
  if (!previousScan) {
    return {
      shouldWarn: false,
      differences: {},
    };
  }

  const differences: SameConditionsCheckResult['differences'] = {};
  let shouldWarn = false;

  // Compare brightness
  const currentBrightness = normalizeBrightness([
    currentPhotos.front,
    currentPhotos.leftSide,
    currentPhotos.rightSide,
  ]);

  const previousBrightness = normalizeBrightness([
    previousScan.front?.metadata,
    previousScan.leftSide?.metadata,
    previousScan.rightSide?.metadata,
  ]);

  if (
    currentBrightness !== undefined &&
    previousBrightness !== undefined
  ) {
    const brightnessDiff = Math.abs(currentBrightness - previousBrightness);
    if (brightnessDiff > 30) {
      shouldWarn = true;
      differences.lighting =
        currentBrightness > previousBrightness ? 'brighter' : 'dimmer';
    } else {
      differences.lighting = 'similar';
    }
  }

  // Compare distance (using resolution as proxy)
  const currentResolution = currentPhotos.front
    ? (currentPhotos.front.width || 0) * (currentPhotos.front.height || 0)
    : undefined;

  const previousResolution = previousScan.front?.metadata
    ? (previousScan.front.metadata.width || 0) *
      (previousScan.front.metadata.height || 0)
    : undefined;

  if (currentResolution !== undefined && previousResolution !== undefined) {
    const resolutionDiff = Math.abs(currentResolution - previousResolution);
    const resolutionRatio = resolutionDiff / previousResolution;
    if (resolutionRatio > 0.2) {
      // More than 20% difference
      shouldWarn = true;
      differences.distance =
        currentResolution > previousResolution ? 'closer' : 'farther';
    } else {
      differences.distance = 'similar';
    }
  }

  // Build warning message
  let message: string | undefined;
  if (shouldWarn) {
    const parts: string[] = [];
    if (differences.lighting && differences.lighting !== 'similar') {
      parts.push(`lighting is ${differences.lighting}`);
    }
    if (differences.distance && differences.distance !== 'similar') {
      parts.push(`distance is ${differences.distance}`);
    }
    if (parts.length > 0) {
      message = `For the most accurate GlowScore™, try to match your previous scan conditions. Your current ${parts.join(' and ')} compared to your last scan.`;
    }
  }

  return {
    shouldWarn,
    message,
    differences,
  };
}




